import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  motion,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

import portfolioStoryItems from "../../../site/data/portfolioStory";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  HORIZONTAL PORTFOLIO STORY
  ============================================================

  Desktop:
  - nuolet
  - hiirellä raahaus
  - vaakasuuntainen scroll

  Mobile:
  - natiivi swipe

  Molemmat:
  - scroll snap
  - aktiivisen kuvan laskuri
  - seuraava kuva jää hieman näkyviin
*/

export default function PortfolioStory() {
  const {
    t,
  } = useTranslation("common");

  const trackRef =
    useRef(null);

  const dragState =
    useRef({
      active: false,
      startX: 0,
      startScrollLeft: 0,
    });

  const scrollFrame =
    useRef(null);

  const [activeIndex, setActiveIndex] =
    useState(0);

  const [isDragging, setIsDragging] =
    useState(false);

  /*
    ------------------------------------------------------------
    Siirrytään tiettyyn korttiin.
    ------------------------------------------------------------
  */

  const scrollToIndex = (
    index
  ) => {
    const track =
      trackRef.current;

    if (!track) {
      return;
    }

    const cards =
      Array.from(
        track.querySelectorAll(
          "[data-portfolio-card]"
        )
      );

    const target =
      cards[index];

    if (!target) {
      return;
    }

    track.scrollTo({
      left:
        target.offsetLeft -
        track.offsetLeft,
      behavior: "smooth",
    });
  };

  /*
    ------------------------------------------------------------
    Päivitetään aktiivinen kuva scrollauksen perusteella.
    ------------------------------------------------------------
  */

  useEffect(() => {
    const track =
      trackRef.current;

    if (!track) {
      return undefined;
    }

    const updateActiveIndex =
      () => {
        const cards =
          Array.from(
            track.querySelectorAll(
              "[data-portfolio-card]"
            )
          );

        if (!cards.length) {
          return;
        }

        const trackLeft =
          track.getBoundingClientRect()
            .left;

        let closestIndex =
          0;

        let closestDistance =
          Infinity;

        cards.forEach(
          (
            card,
            index
          ) => {
            const cardLeft =
              card.getBoundingClientRect()
                .left;

            const distance =
              Math.abs(
                cardLeft -
                  trackLeft
              );

            if (
              distance <
              closestDistance
            ) {
              closestDistance =
                distance;

              closestIndex =
                index;
            }
          }
        );

        setActiveIndex(
          closestIndex
        );
      };

    const handleScroll =
      () => {
        if (
          scrollFrame.current
        ) {
          cancelAnimationFrame(
            scrollFrame.current
          );
        }

        scrollFrame.current =
          requestAnimationFrame(
            updateActiveIndex
          );
      };

    track.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    updateActiveIndex();

    return () => {
      track.removeEventListener(
        "scroll",
        handleScroll
      );

      if (
        scrollFrame.current
      ) {
        cancelAnimationFrame(
          scrollFrame.current
        );
      }
    };
  }, []);

  /*
    ------------------------------------------------------------
    Desktop-hiiriraahaus.
    Touch-laitteet käyttävät selaimen omaa swipea.
    ------------------------------------------------------------
  */

  const handlePointerDown =
    (event) => {
      if (
        event.pointerType !==
        "mouse"
      ) {
        return;
      }

      const track =
        trackRef.current;

      if (!track) {
        return;
      }

      dragState.current = {
        active: true,
        startX:
          event.clientX,
        startScrollLeft:
          track.scrollLeft,
      };

      setIsDragging(true);

      track.setPointerCapture?.(
        event.pointerId
      );
    };

  const handlePointerMove =
    (event) => {
      if (
        !dragState.current
          .active
      ) {
        return;
      }

      const track =
        trackRef.current;

      if (!track) {
        return;
      }

      const distance =
        event.clientX -
        dragState.current
          .startX;

      track.scrollLeft =
        dragState.current
          .startScrollLeft -
        distance;
    };

  const stopDragging =
    (event) => {
      if (
        !dragState.current
          .active
      ) {
        return;
      }

      dragState.current.active =
        false;

      setIsDragging(false);

      trackRef.current
        ?.releasePointerCapture?.(
          event.pointerId
        );
    };

  const previous =
    () => {
      scrollToIndex(
        Math.max(
          0,
          activeIndex - 1
        )
      );
    };

  const next =
    () => {
      scrollToIndex(
        Math.min(
          portfolioStoryItems.length -
            1,
          activeIndex + 1
        )
      );
    };

  const progress =
    ((activeIndex + 1) /
      portfolioStoryItems.length) *
    100;

  return (
    <section
      className="
        relative
        overflow-hidden
        border-t
        border-border
        bg-canvas
        py-24

        sm:py-32
        lg:py-40
      "
    >
      {/* ======================================================
          SECTION HEADER
          ====================================================== */}

      <div
        className="
          mx-auto
          mb-12
          flex
          w-full
          max-w-editorial
          items-end
          justify-between
          gap-8
          px-6

          sm:mb-16
          sm:px-10

          lg:px-16
        "
      >
        <motion.div
          initial={{
            opacity: 0,
            y: 24,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.4,
          }}
          transition={{
            duration: 0.8,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              mb-5
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:text-[10px]
            "
          >
            02 /{" "}
            {t(
              "portfolioStory.eyebrow"
            )}
          </p>

          <h2
            className="
              m-0
              max-w-4xl
              font-display
              text-[clamp(2.8rem,6vw,6.5rem)]
              font-normal
              leading-[0.9]
              tracking-[-0.045em]
              text-text
            "
          >
            {t(
              "portfolioStory.title"
            )}
          </h2>
        </motion.div>

        {/* Desktop controls */}

        <div
          className="
            hidden
            shrink-0
            items-center
            gap-2

            lg:flex
          "
        >
          <button
            type="button"
            onClick={previous}
            disabled={
              activeIndex === 0
            }
            className="
              flex
              h-12
              w-12
              items-center
              justify-center
              rounded-full
              border
              border-border
              text-lg
              text-text
              transition
              duration-300

              hover:bg-text
              hover:text-canvas

              disabled:cursor-default
              disabled:opacity-25
              disabled:hover:bg-transparent
              disabled:hover:text-text
            "
            aria-label={t(
              "portfolioStory.previous"
            )}
          >
            ←
          </button>

          <button
            type="button"
            onClick={next}
            disabled={
              activeIndex ===
              portfolioStoryItems.length -
                1
            }
            className="
              flex
              h-12
              w-12
              items-center
              justify-center
              rounded-full
              border
              border-border
              text-lg
              text-text
              transition
              duration-300

              hover:bg-text
              hover:text-canvas

              disabled:cursor-default
              disabled:opacity-25
              disabled:hover:bg-transparent
              disabled:hover:text-text
            "
            aria-label={t(
              "portfolioStory.next"
            )}
          >
            →
          </button>
        </div>
      </div>

      {/* ======================================================
          HORIZONTAL TRACK
          ====================================================== */}

      <motion.div
        initial={{
          opacity: 0,
          y: 30,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
          amount: 0.15,
        }}
        transition={{
          duration: 0.95,
          delay: 0.08,
          ease: [
            0.22,
            1,
            0.36,
            1,
          ],
        }}
      >
        <div
          ref={trackRef}
          onPointerDown={
            handlePointerDown
          }
          onPointerMove={
            handlePointerMove
          }
          onPointerUp={
            stopDragging
          }
          onPointerCancel={
            stopDragging
          }
          onPointerLeave={
            stopDragging
          }
          className={`
            flex
            snap-x
            snap-mandatory
            gap-4
            overflow-x-auto
            px-6
            pb-3

            sm:gap-6
            sm:px-10

            lg:px-16

            [scrollbar-width:none]
            [&::-webkit-scrollbar]:hidden

            ${
              isDragging
                ? "cursor-grabbing select-none scroll-auto"
                : "cursor-grab scroll-smooth"
            }
          `}
        >
          {portfolioStoryItems.map(
            (
              item,
              index
            ) => (
              <article
                key={item.id}
                data-portfolio-card
                className="
                  w-[86vw]
                  shrink-0
                  snap-start

                  sm:w-[76vw]
                  lg:w-[68vw]
                  xl:w-[62vw]
                "
              >
                <div
                  className="
                    relative
                    aspect-[4/3]
                    overflow-hidden
                    bg-surface

                    sm:aspect-[16/10]
                  "
                >
                  <img
                    src={item.src}
                    alt={t(
                      `portfolioStory.items.${item.translationKey}.alt`
                    )}
                    draggable="false"
                    className="
                      h-full
                      w-full
                      object-cover
                      transition-transform
                      duration-700

                      lg:hover:scale-[1.015]
                    "
                  />

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-x-0
                      bottom-0
                      h-1/3
                      bg-gradient-to-t
                      from-black/35
                      to-transparent
                    "
                    aria-hidden="true"
                  />

                  <div
                    className="
                      absolute
                      bottom-5
                      left-5
                      right-5
                      flex
                      items-end
                      justify-between
                      gap-5
                      text-white

                      sm:bottom-7
                      sm:left-7
                      sm:right-7
                    "
                  >
                    <div>
                      <p
                        className="
                          mb-2
                          text-[9px]
                          uppercase
                          tracking-[0.24em]
                          text-white/75

                          sm:text-[10px]
                        "
                      >
                        {t(
                          `portfolioStory.items.${item.translationKey}.category`
                        )}
                      </p>

                      <p
                        className="
                          m-0
                          font-display
                          text-2xl
                          leading-none

                          sm:text-3xl
                        "
                      >
                        {t(
                          `portfolioStory.items.${item.translationKey}.title`
                        )}
                      </p>
                    </div>

                    <span
                      className="
                        shrink-0
                        font-display
                        text-2xl
                        italic
                        text-white/85
                      "
                    >
                      {item.id}
                    </span>
                  </div>
                </div>
              </article>
            )
          )}

          {/* pieni loppupadding */}
          <div
            className="
              w-2
              shrink-0

              sm:w-6
            "
            aria-hidden="true"
          />
        </div>
      </motion.div>

      {/* ======================================================
          PROGRESS / MOBILE SWIPE HINT
          ====================================================== */}

      <div
        className="
          mx-auto
          mt-8
          flex
          w-full
          max-w-editorial
          items-center
          gap-6
          px-6

          sm:px-10
          lg:px-16
        "
      >
        <p
          className="
            m-0
            shrink-0
            text-[9px]
            uppercase
            tracking-[0.2em]
            text-muted

            sm:text-[10px]
          "
        >
          {String(
            activeIndex + 1
          ).padStart(
            2,
            "0"
          )}
          {" / "}
          {String(
            portfolioStoryItems.length
          ).padStart(
            2,
            "0"
          )}
        </p>

        <div
          className="
            relative
            h-px
            flex-1
            overflow-hidden
            bg-border
          "
        >
          <div
            className="
              absolute
              inset-y-0
              left-0
              bg-text
              transition-[width]
              duration-500
              ease-out
            "
            style={{
              width:
                `${progress}%`,
            }}
          />
        </div>

        <p
          className="
            m-0
            shrink-0
            text-[9px]
            uppercase
            tracking-[0.2em]
            text-muted

            lg:hidden
          "
        >
          {t(
            "portfolioStory.swipe"
          )}
          {" →"}
        </p>
      </div>
    </section>
  );
}
